// Page orientation for certificates.
//
// It cannot live in the global stylesheet. `@page` takes page descriptors and
// margin at-rules, not selectors, so nothing about the page box can be scoped to
// a class — `@page { size: landscape }` in summary.scss would rotate incident
// summaries too, which share it. react-to-print's `pageStyle` injects a
// stylesheet into the print iframe for one print job only, which is exactly the
// scope needed. Everything here therefore affects certificates and nothing else.
//
// **Supplying `pageStyle` replaces the library's default wholesale**, and that
// default carried two things worth keeping: the colour-adjust rules that tell
// browsers to print background colours, and `@page { margin: 0 }`. Both are
// restated below. Reducing this to the orientation rule would look like a
// tidy-up and would quietly print certificates without their colour, headed and
// footed by the browser.
//
// **Ordering matters and is not obvious.** react-to-print appends this
// stylesheet to the iframe head *before* it copies the app's own sheets in, so
// on equal specificity the app wins. `summary.scss` sets `@page { margin: 2cm }`
// for print, which is why the margin reset below has to be `!important` to hold.
// Rules the app does not target at all — the padding on the certificate root —
// need no such help.
export const CERTIFICATE_PAGE_STYLE = `
  @page {
    size: landscape;

    /* Browsers draw their own header and footer — timestamp, page title, URL,
       page number — into the page margin, and omit them when there is no margin
       to draw them in. The 2cm inset the certificate still wants is applied as
       padding on the content instead, below.

       This is a hint rather than a guarantee: the print dialog's own "Headers
       and footers" setting overrides it in some browsers, and that is the only
       reliable way to be rid of them. */
    margin: 0 !important;
  }

  @media print {
    body {
      -webkit-print-color-adjust: exact;
      color-adjust: exact;

      /* Vertical centring on the page. react-to-print appends the cloned
         certificate straight to this body, so it is the only box that spans the
         sheet and the only place this can be done from.

         Print is the one context where vh is the page box rather than a
         viewport, which is what makes it work here and also what makes it worth
         checking in a real print preview rather than trusting. Remove these four
         declarations and the certificate returns to sitting at the top. */
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-height: 100vh;
    }

    /* The inset the page margin used to provide. It sits here rather than on
       body because index.scss sets body { padding: 0 } outside any media
       query, and that is copied in after this stylesheet. Nothing in the app
       targets this class at all, so this holds without fighting for it. */
    .certificate-of-achievement {
      box-sizing: border-box;
      padding: 2cm;
      width: 100%;
    }
  }
`;
